import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { assetUrl, SafeImage } from "@/lib/assetUrl";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Wand2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

// Fase 28 · re-exportamos assetUrl para compat con imports existentes.
export { assetUrl };

/**
 * Botón + Dialog para elegir una pieza APROBADA de la galería del Motor Creativo.
 * Props:
 *  - onSelect(piece): callback con la pieza aprobada seleccionada
 *  - enabled: si el usuario tiene motor_creativo (si no, no renderiza)
 *  - testid: prefijo opcional
 */
export default function GalleryPicker({ onSelect, enabled = true, testid = "gallery-picker" }) {
  const [open, setOpen] = useState(false);
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/creative/pieces", { params: { status: "aprobada" } });
      setPieces(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar la galería");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async () => {
    setOpen(true);
    await load();
  };

  const pick = (p) => {
    onSelect?.(p);
    setOpen(false);
    toast.success("Pieza asignada");
  };

  if (!enabled) return null;

  return (
    <>
      <Button type="button" variant="outline" onClick={openDialog} data-testid={`${testid}-open`}>
        <Wand2 className="h-4 w-4 mr-1.5" /> Elegir de la galería
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-brand-emerald" /> Galería del Motor Creativo
            </DialogTitle>
            <DialogDescription>
              Solo se muestran piezas <strong>aprobadas</strong>. Haga clic en la que
              desea asignar.
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
          ) : pieces.length === 0 ? (
            <div className="p-12 text-center text-stone-500">
              Sin piezas aprobadas aún. Genere una desde el Motor Creativo.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
              {pieces.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => pick(p)}
                  data-testid={`${testid}-piece-${p.id}`}
                  className="group relative rounded-lg overflow-hidden border border-border hover:border-brand-emerald hover:shadow-emerald transition-all"
                >
                  <SafeImage src={p.file_url} alt={p.prompt} className="w-full aspect-square object-cover" testid={`${testid}-piece-image-${p.id}`} />
                  <div className="absolute inset-0 bg-brand-ink/0 group-hover:bg-brand-ink/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 bg-brand-gold text-brand-ink font-semibold rounded-full px-3 py-1.5 text-sm flex items-center gap-1">
                      <Check className="h-4 w-4" /> Elegir
                    </div>
                  </div>
                  <div className="p-2 text-xs text-stone-600 line-clamp-2 bg-white">{p.prompt}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
